import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { CompanySectionApiService } from 'app/services/api/company-section-api.service';
import { ErrorElementsApiService } from 'app/services/api/error-elements-api.service';
import { ErrorLevelApiService } from 'app/services/api/error-level-api.service';
import { ErrorTypesApiService } from 'app/services/api/error-types-api.service';
import { CompanyApiService } from 'app/services/api/firms-api.service';
import { MissionsApiService } from 'app/services/api/missions-api.service';
import { NotificationApiService } from 'app/services/api/notification-api.service';
import { ReportTemplateApiService } from 'app/services/api/report-template-api.service';
import { ReportVersioningApiService } from 'app/services/api/report-versioning-api.service';
import { StandartsApiService } from 'app/services/api/standarts-api.service';
import { UsersApiService } from 'app/services/api/users-api.service';
import { WarehouseApiService } from 'app/services/api/warehouse-api.service';
import { WarehouseReportApiService } from 'app/services/api/warehouse-report-api.service';
import { WarehouseReportErrorApiService } from 'app/services/api/warehouse-report-error-api.service';
import { WarehouseReportMetaApiService } from 'app/services/api/warehouse-report-meta-api.service';
import { CacheKeyService } from 'app/services/cache-key.service';
import { SyncService } from 'app/services/sync.service';
import { appDb } from 'dexie/app.db';
import { combineLatest, from, map, Observable, of, switchMap, tap } from 'rxjs';

export const cacheKeyGuard: CanActivateFn = (route, state) => {
  const cacheKeyService = inject(CacheKeyService);
  const companyApiService = inject(CompanyApiService);
  const companySectionApiService = inject(CompanySectionApiService);
  const usersApiService = inject(UsersApiService);
  const warehouseApiService = inject(WarehouseApiService);
  const errorLevelApiService = inject(ErrorLevelApiService);
  const errorTypesApiService = inject(ErrorTypesApiService);
  const errorElementsApiService = inject(ErrorElementsApiService);
  const standartsApiService = inject(StandartsApiService);
  const syncService = inject(SyncService);
  const userService = inject(UserService);
  const missionsApiService = inject(MissionsApiService);
  const warehouseReportApiService = inject(WarehouseReportApiService);
  const warehouseReportErrorApiService = inject(WarehouseReportErrorApiService);
  const warehouseReportMetaApiService = inject(WarehouseReportMetaApiService);
  const reportTemplateApiSerive = inject(ReportTemplateApiService);
  const userNotificationApiSerive = inject(NotificationApiService);
  const reportVersioningApiSerive = inject(ReportVersioningApiService);

  let dbOpen = of(true);
  if (!appDb.isOpen()) {
    dbOpen = from(
      appDb.open().then((dexie) => {
        return dexie.isOpen();
      })
    );
  }
  const services = {
    companyKey: (c) => companyApiService.getFirms(c),
    companySectionKey: (c) => companySectionApiService.getCompanySections(c),
    userKey: (c) => usersApiService.getUsers(c),
    wareHouseKey: (c) => warehouseApiService.getWareHouseList(c),
    errorLevelKey: (c) => errorLevelApiService.getList(c),
    typeErrorKey: (c) => errorTypesApiService.getList(c),
    standardKey: (c) => standartsApiService.getList(c),
    userMissionKey: (c) => missionsApiService.getMissions(c),
    wareHouseReportKey: (c) => warehouseReportApiService.getList(c),
    errorElementKey: (c) => errorElementsApiService.getList(c),
    wareHouseReportError: (c) => warehouseReportErrorApiService.getList(c),
    reportTemplateKey: (c) => reportTemplateApiSerive.getList(c),
    userNotificationKey: (c) => userNotificationApiSerive.getList(c),
    reportVersioningKey: (c) => reportVersioningApiSerive.getList(c),
  };
  return dbOpen.pipe(
    switchMap(() =>
      userService.get().pipe(
        switchMap((user: any) => {
          if (user?.role === 'User') {
            services.userKey = (c) =>
              from(
                appDb.user.put({ ...user, isDeleted: user.isDeleted ? 1 : 0 })
              );
          }
          return cacheKeyService.checkAndUpdateTables(undefined, services).pipe(
            switchMap((res) =>
              res.length
                ? combineLatest([
                    ...(res.map(
                      (item) =>
                        services[item.id]?.(item.oldHash || '') || of(null)
                    ) as Array<Observable<any>>),
                  ]).pipe(map(() => true))
                : of(true)
            ),
            switchMap((res) =>
              warehouseReportMetaApiService.getMeta().pipe(map(() => res))
            ),
            tap(() => {
              syncService.startCountdown();
            })
          );
        })
      )
    )
  );
};
